import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music2, Gift, Plus, Copy, Check, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import useAuthStore from '../store/useAuthStore';

const AdminCodes = () => {
  const navigate = useNavigate();
  const { user, token, createAccessCode, getAccessCodes } = useAuthStore();
  
  const [codes, setCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [newCode, setNewCode] = useState({ customCode: '', maxUses: 1, expiresDays: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      loadCodes();
    }
  }, [token]);

  const loadCodes = async () => {
    try {
      const data = await getAccessCodes();
      setCodes(data.codes || []);
    } catch (error) {
      console.error('Failed to load codes:', error);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const result = await createAccessCode(
        parseInt(newCode.maxUses) || 1,
        newCode.expiresDays ? parseInt(newCode.expiresDays) : null,
        newCode.customCode || null
      );
      
      setCodes([{ ...result, current_uses: 0, created_at: new Date().toISOString() }, ...codes]);
      setNewCode({ customCode: '', maxUses: 1, expiresDays: '' });
    } catch (error) {
      setError(error.message);
    }
    setIsLoading(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Button onClick={() => navigate('/auth')}>Please sign in</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg p-6" data-testid="admin-codes-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Access Codes</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/practice')}>
            Back to Practice
          </Button>
        </div>

        {/* Create New Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border-2 border-border rounded-2xl p-6 mb-6"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Create New Code
          </h2>
          
          <form onSubmit={handleCreateCode} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="customCode">Custom Code (optional)</Label>
                <Input
                  id="customCode"
                  placeholder="FREEBONUS"
                  value={newCode.customCode}
                  onChange={(e) => setNewCode({ ...newCode, customCode: e.target.value.toUpperCase() })}
                  className="uppercase"
                  data-testid="custom-code-input"
                />
              </div>
              <div>
                <Label htmlFor="maxUses">Max Uses</Label>
                <Input
                  id="maxUses"
                  type="number"
                  min="1"
                  value={newCode.maxUses}
                  onChange={(e) => setNewCode({ ...newCode, maxUses: e.target.value })}
                  data-testid="max-uses-input"
                />
              </div>
              <div>
                <Label htmlFor="expiresDays">Expires in (days)</Label>
                <Input
                  id="expiresDays"
                  type="number"
                  min="1"
                  placeholder="Never"
                  value={newCode.expiresDays}
                  onChange={(e) => setNewCode({ ...newCode, expiresDays: e.target.value })}
                  data-testid="expires-input"
                />
              </div>
            </div>
            
            {error && <p className="text-sm text-destructive">{error}</p>}
            
            <Button type="submit" disabled={isLoading} className="bg-accent hover:bg-accent/90" data-testid="create-code-btn">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Code'}
            </Button>
          </form>
        </motion.div>

        {/* Existing Codes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border-2 border-border rounded-2xl p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Your Codes</h2>
          
          {codes.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No access codes created yet. Create one above!
            </p>
          ) : (
            <div className="space-y-3">
              {codes.map((code, i) => (
                <div
                  key={code.code}
                  className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl"
                  data-testid={`code-item-${i}`}
                >
                  <div>
                    <div className="font-mono font-bold text-lg">{code.code}</div>
                    <div className="text-sm text-muted-foreground">
                      {code.current_uses}/{code.max_uses} uses
                      {code.expires_at && ` • Expires ${new Date(code.expires_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyCode(code.code)}
                    data-testid={`copy-code-${i}`}
                  >
                    {copiedCode === code.code ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Usage Instructions */}
        <div className="mt-6 p-4 bg-secondary/30 rounded-xl text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-2">How to use access codes:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Share the code with users you want to give free access</li>
            <li>They can redeem it on the subscription page or /redeem</li>
            <li>Each code can be used up to the "Max Uses" limit</li>
            <li>Great for bonuses, lead magnets, or promotional giveaways!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminCodes;
